// Phase G4-A — content provenance metadata for ChildAttempt records.
//
// The child_attempts table (migration 0005) has four NOT NULL
// reproducibility columns the spec's ChildAttempt requires:
// generator_version, validator_version, curriculum_version,
// content_hash. Pencilwise's content packs don't carry these per-item,
// and no version constants existed in the repo. This module is the
// single source so every attempt insert satisfies the constraints.
//
// CALIBRATION VALUES (per the working agreement's
// calibration-value-disclosure rule): the version strings below are
// provenance labels, not externally-sourced. They identify which
// engine/content generation produced an attempt so a future
// migration or audit can trace it. They are NOT semantic version
// guarantees — bump them when the generator/validator logic or the
// curriculum content materially changes.

// pencilwise package.json version at the time the G4 content slice
// landed. Curriculum content is versioned with the app for now;
// Phase H may split a dedicated curriculum-version stamp.
export const CURRICULUM_VERSION = "2.0.0";

// The G3/G4 ports consume engine-authored + bundle-authored content.
// "g4-slice" marks attempts produced against the launch_safe vertical
// slice; broad content in Phase H bumps this.
const GENERATOR_VERSION = "g4-slice-1";

// Matches the curriculum validator suite version that gated the
// content (the Zod rules in src/curriculum/validators/*). Bump when
// the validator rules change.
const VALIDATOR_VERSION = "phase-b-1.0";

/** Deterministic, dependency-free string hash (FNV-1a, 32-bit, hex).
 * Used to stamp content_hash on an attempt so the exact item content
 * the kid saw is traceable. NOT cryptographic — collision-resistant
 * enough for content-identity auditing. Stable across runs/browsers
 * (no crypto.subtle async, no salt). */
function contentHash(input: string): string {
  let h = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    // FNV prime multiply, kept in 32-bit range via Math.imul
    h = Math.imul(h, 0x01000193);
  }
  // unsigned 32-bit hex
  return (h >>> 0).toString(16).padStart(8, "0");
}

/** Build the content_hash for a specific item by hashing its stable
 * JSON. Callers pass the item object (or its id + prompt) — anything
 * uniquely identifying the content the kid saw. */
function contentHashForItem(item: unknown): string {
  return contentHash(JSON.stringify(item));
}

/** The four reproducibility fields bundled, for spreading into a
 * ChildAttempt. `contentHash` is per-item, so this takes the item. */
export function contentProvenance(item: unknown): {
  generatorVersion: string;
  validatorVersion: string;
  curriculumVersion: string;
  contentHash: string;
} {
  return {
    generatorVersion: GENERATOR_VERSION,
    validatorVersion: VALIDATOR_VERSION,
    curriculumVersion: CURRICULUM_VERSION,
    contentHash: contentHashForItem(item),
  };
}
